/**
 * SignupPage — Cognito registration for external (non-Okta) users.
 * Two steps: 1) Email + password form, 2) Verification code confirmation.
 * Supports pre-filled email and inviteId from query params.
 */

import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Container, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import { signUp, confirmSignUp, signIn } from "aws-amplify/auth";
import { isNepEmail } from "../authMode";

type Step = "register" | "verify";

export default function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const prefillEmail = searchParams.get("email") || "";
  const inviteId = searchParams.get("inviteId") || "";

  const [step, setStep] = useState<Step>("register");
  const [email, setEmail] = useState(prefillEmail);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSignUp = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (isNepEmail(email)) {
      setError("NEP Group users (@nepgroup.com) must use Okta SSO. Please go to the login page.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    setIsSubmitting(true);
    try {
      await signUp({
        username: email.toLowerCase(),
        password,
        options: {
          userAttributes: {
            email: email.toLowerCase(),
            given_name: firstName.trim(),
            family_name: lastName.trim(),
          },
        },
      });
      setStep("verify");
    } catch (err: any) {
      if (err.name === "UsernameExistsException") {
        setError("An account with this email already exists. Please sign in instead.");
      } else {
        setError(err.message || "Sign up failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await confirmSignUp({
        username: email.toLowerCase(),
        confirmationCode: verificationCode,
      });

      // Auto-login after verification
      await signIn({
        username: email.toLowerCase(),
        password,
      });

      // Redirect: if from invite, go back to accept; otherwise go to dashboard
      if (inviteId) {
        navigate(`/invite/${inviteId}`);
      } else {
        navigate("/");
      }
    } catch (err: any) {
      if (err.name === "CodeMismatchException") {
        setError("Invalid verification code. Please check your email and try again.");
      } else if (err.name === "ExpiredCodeException") {
        setError("Verification code expired. Please request a new one.");
      } else {
        setError(err.message || "Verification failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
      <Card style={{ width: "100%", maxWidth: "440px" }}>
        <Card.Body className="p-4">
          <div className="text-center mb-4">
            <img
              src="/nep-logo.png"
              alt="NEP Group"
              className="mb-3"
              style={{ maxWidth: "160px", height: "auto" }}
            />
            <h2 className="fw-bold" style={{ color: "#0e67f5" }}>AnyPlay</h2>
            <p className="text-muted">
              {step === "register" ? "Create Account" : "Verify Email"}
            </p>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {step === "register" && (
            <Form onSubmit={handleSignUp}>
              <Form.Group className="mb-3">
                <Form.Label>Email address</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  disabled={!!prefillEmail}
                />
                {prefillEmail && (
                  <Form.Text className="text-muted">
                    Email is pre-filled from your invitation.
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Confirm Password</Form.Label>
                <Form.Control
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  required
                />
              </Form.Group>

              <Button
                variant="primary"
                type="submit"
                className="w-100 mb-3"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <><Spinner animation="border" size="sm" className="me-2" /> Creating Account...</>
                ) : (
                  "Create Account"
                )}
              </Button>

              <div className="text-center">
                <small className="text-muted">
                  Already have an account?{" "}
                  <Link to="/login">Sign In</Link>
                </small>
              </div>
            </Form>
          )}

          {step === "verify" && (
            <Form onSubmit={handleVerify}>
              <Alert variant="info">
                We've sent a verification code to <strong>{email}</strong>. Please check your inbox (and spam folder).
              </Alert>

              <Form.Group className="mb-3">
                <Form.Label>Verification Code</Form.Label>
                <Form.Control
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  required
                  maxLength={6}
                  autoFocus
                />
              </Form.Group>

              <Button
                variant="primary"
                type="submit"
                className="w-100 mb-3"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <><Spinner animation="border" size="sm" className="me-2" /> Verifying...</>
                ) : (
                  "Verify & Sign In"
                )}
              </Button>

              <div className="text-center">
                <small className="text-muted">
                  Didn't receive a code? Check your spam folder or{" "}
                  <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-none"
                    style={{ fontSize: "inherit" }}
                    onClick={() => { setStep("register"); setError(null); }}
                  >
                    try again
                  </button>
                </small>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
